import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../../assets/images/close_Icon.svg";
import { REACT_EMPLOYEE_LOGIN_SCREEN } from "../../constants/ReactEndPoints";
import { getFunctionKeyDetails, removeLoggedInData, updateObjectInArray } from "../../util/helperFunction";
import { uploadAttRecords } from "../../services/FunctionKeysService";
import { selectCurrentFunctionKey, selectProvision } from "../../feature/Dashboard/dashboardSelectors";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import CommonUtil from "../../util/CommonUtils";
import { useTranslation } from "react-i18next";

const INACTIVITY_TIMEOUT = 15000;

const PunchLockoutPage = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    message = "",
    lockoutType = "",
    punchPayload = {},
    issueTypeCode = 0,
    isLoggedPunchData = false,
  } = location.state || {};

  const currentTime = punchPayload?.[0]?.punchDate || Date.now();
  const { menuIcon, label } = useSelector(selectCurrentFunctionKey);

  useEffect(() => {
    if (!CommonUtil.isTruthyValue(localStorage.getItem("isLoggedIn"))) {
      navigate(REACT_EMPLOYEE_LOGIN_SCREEN, { replace: true });
    }
  }, [navigate]);

  const logout = useCallback(() => {
    removeLoggedInData();
    navigate(REACT_EMPLOYEE_LOGIN_SCREEN, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const timeout = setTimeout(logout, INACTIVITY_TIMEOUT);
    return () => clearTimeout(timeout);
  }, [logout]);

  const submitPunchData = useCallback((updatedPayload) => {
    uploadAttRecords(updatedPayload)
      .then((res) => {
        if (res?.status === 201 && res?.data?.message === "Success") {
          console.info("Punch submitted silently with issue type.");
        }
      })
      .catch((error) => {
        console.error("Error while submitting punch silently:", error);
      });
  }, []);

  useEffect(() => {
    if (issueTypeCode !== 0 && CommonUtil.isTruthyValue(isLoggedPunchData)) {
      const updatedData = updateObjectInArray(punchPayload, { issueType: issueTypeCode }, 0);
      submitPunchData({ data: updatedData });
    }
  }, [issueTypeCode]);

  const { formattedDate, formattedTime, formattedAmPm } = useMemo(() => {
    const date = new Date(currentTime);
    return {
      formattedDate: format(date, "EEEE, MMMM do, yyyy"),
      formattedTime: format(date, "hh:mm:ss"),
      formattedAmPm: format(date, "a"),
    };
  }, [currentTime]);

  const renderDateTime = useCallback(
    () => (
      <Box>
        <Box
          sx={{
            paddingBottom: "10px",
            fontSize: "52px",
            fontFamily: "inter",
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            fontWeight: "700",
            "@media (max-width: 1800px)": { fontSize: "28px" },
          }}
        >
          {formattedTime}
          <span style={{ paddingLeft: "10px", fontFamily: "Inter" }}>{formattedAmPm}</span>
        </Box>

        <Box
          sx={{
            fontSize: "48px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            fontWeight: "500",
            "@media (max-width: 1800px)": { fontSize: "14px" },
          }}
        >
          <span style={{ fontSize: "21px", fontFamily: "inter" }}>{formattedDate}</span>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                margin: "5px auto 0px auto",
                color: "red",
                fontSize: "16px",
                fontFamily: "inter",
              }}
            >
              {lockoutType}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontFamily: "inter", color: "red" }}>{message}</Typography>
          </Box>
        </Box>
      </Box>
    ),
    [formattedDate, formattedTime, formattedAmPm, label, message]
  );

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "center",
        padding: "26px 25px 0px 25px",
      }}
    >
      <Grid item xs={12} md={6} lg={7} xl={7}>
        <Box
          sx={{
            width: "100%",
            padding: "10px 5px",
            position: "relative",
            borderRadius: "12px",
            backgroundColor: "rgba(206, 227, 243, 1)",
            boxShadow: "0px 0px 24px 10px rgba(0, 0, 0, 0.25)",
          }}
        >
          <IconButton sx={{ position: "absolute", right: 10, top: 10 }} onClick={logout} aria-label="close">
            <CloseIcon style={{ color: "#000" }} />
          </IconButton>

          {renderDateTime()}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              paddingTop: "10px",
              width: "100%",
            }}
          >
            <Box textAlign="center">
              <Box
                sx={{
                  p: { xs: 1, md: 2 },
                  backgroundColor: "#000",
                  borderRadius: "12px",
                  display: "inline-block",
                }}
              >
                <img
                  src={getFunctionKeyDetails(menuIcon)?.src}
                  alt="icon"
                  style={{ width: "60px", height: "50px", cursor: "pointer" }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "12px", md: "16px" },
                  // pt: "10px",
                  fontFamily: "Inter",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: "150px", sm: "250px", lg: "300px", xl: "450px" },
              }}
            >
              <Grid container alignItems="center" pb={1}>
                <Grid item xs={7}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: "12px", md: "18px" },
                      fontFamily: "Inter",
                    }}
                  >
                  {t("punchLockout.labels.employeeId")}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: "12px", md: "18px" },
                      fontFamily: "Inter",
                    }}
                  >
                    {localStorage.getItem("employeeNumber")}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default PunchLockoutPage;
